/**
 * Accounting Configuration Constants
 *
 * This file defines the available organizations and cost centers for each accounting system.
 * These are the values that can be selected in the admin UI when configuring venue accounting.
 */

export interface OrganizationConfig {
  id: string;
  name: string;
}

export interface CostCenterConfig {
  id: string;
  name: string;
}

export interface AccountingSystemConfig {
  organizations: OrganizationConfig[];
  costCenters: Record<string, CostCenterConfig[]>;
}

// ============================================================================
// FORTNOX CONFIGURATION
// Using actual Fortnox organization IDs and cost center codes
// ============================================================================

export const FORTNOX_CONFIG: AccountingSystemConfig = {
  organizations: [
    { id: "556790-7893", name: "OnAir-Production" },
    // Add more organizations here with their actual Fortnox org IDs
  ],
  costCenters: {
    "556790-7893": [
      { id: "KPH", name: "Copenhagen" },
      { id: "ONAIR", name: "OnAir (Game Show) Stockholm" },
      { id: "ONAIRG", name: "OnAir (Game Show) Gothenburg" },
      { id: "OSLO", name: "OSLO" },
    ],
    // Add cost centers for other organizations here
  },
};

// ============================================================================
// E-CONOMIC CONFIGURATION
// ============================================================================

export const ECONOMIC_CONFIG: AccountingSystemConfig = {
  organizations: [
    { id: "ECONOMIC_ORG_COPENHAGEN", name: "Copenhagen Office" },
    { id: "ECONOMIC_ORG_AARHUS", name: "Aarhus Office" },
  ],
  costCenters: {
    ECONOMIC_ORG_COPENHAGEN: [
      { id: "COPENHAGEN_MAIN", name: "Main Operations" },
      { id: "COPENHAGEN_VIP", name: "VIP Services" },
    ],
    ECONOMIC_ORG_AARHUS: [{ id: "AARHUS_MAIN", name: "Main Operations" }],
  },
};

// ============================================================================
// POWEROFFICE CONFIGURATION
// ============================================================================

export const POWEROFFICE_CONFIG: AccountingSystemConfig = {
  organizations: [
    { id: "ONAIR_NORWAY", name: "On Air Game Shows Norway AS" },
    // Add more PowerOffice organizations here as needed
  ],
  costCenters: {
    ONAIR_NORWAY: [
      { id: "OSLO", name: "Oslo" },
      { id: "BERGEN", name: "Bergen" },
      { id: "TRONDHEIM", name: "Trondheim" },
      // Add more Norwegian cost centers as needed
    ],
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get accounting configuration for a specific system
 */
export function getAccountingConfig(
  system: "fortnox" | "economic" | "poweroffice",
): AccountingSystemConfig {
  switch (system) {
    case "fortnox":
      return FORTNOX_CONFIG;
    case "economic":
      return ECONOMIC_CONFIG;
    case "poweroffice":
      return POWEROFFICE_CONFIG;
    default:
      throw new Error(`Unknown accounting system: ${system}`);
  }
}

/**
 * Get cost centers for a specific organization
 */
export function getCostCentersForOrganization(
  system: "fortnox" | "economic" | "poweroffice",
  organizationId: string,
): CostCenterConfig[] {
  const config = getAccountingConfig(system);
  return config.costCenters[organizationId] || [];
}

/**
 * Validate that an organization ID exists for a system
 */
export function isValidOrganization(
  system: "fortnox" | "economic" | "poweroffice",
  organizationId: string,
): boolean {
  const config = getAccountingConfig(system);
  return config.organizations.some((org) => org.id === organizationId);
}

/**
 * Validate that a cost center exists for an organization
 */
export function isValidCostCenter(
  system: "fortnox" | "economic" | "poweroffice",
  organizationId: string,
  costCenterId: string,
): boolean {
  const costCenters = getCostCentersForOrganization(system, organizationId);
  return costCenters.some((cc) => cc.id === costCenterId);
}
